import React, { useState } from 'react'
import { useUserTranslationsQuery } from '@graphql/queries/__generated__/user-translation.generated'
import makeStyles from '@mui/styles/makeStyles'
import TranslateIcon from '@mui/icons-material/Translate'
import IconButton from '@mui/material/IconButton'
import { useTranslation } from 'react-i18next'
import { maybe } from '../../utils/misc'
import { UserTranslationFragment } from '@graphql/fragments/__generated__/UserTranslation.generated'
import { TranslationStatusEnum } from '../../../generated/gqlTypes'
import Tooltip, { tooltipClasses } from '@mui/material/Tooltip'
import { USER_TRANSLATION_STATUS_MAPPING } from '../../../mappings'
import UserTranslationDialogForm, { TranslationFormData } from '@components/UserTranslation/UserTranslationDialogForm'
import { useUpdateTranslationMutation } from '@graphql/mutations/__generated__/user-translation.generated'
import { useSnackbar } from 'notistack'
import { Theme } from '@mui/material'
import { useMeQuery } from '@graphql/queries/__generated__/me.generated'

const useStyles = makeStyles((theme: Theme) => ({
  popper: {
    [`& .${tooltipClasses.tooltip}`]: {
      backgroundColor: theme.palette.common.white,
      color: 'rgba(0, 0, 0, 0.87)',
      boxShadow: theme.shadows[1],
      fontSize: 11,
    },
  },
}))

interface Props {
  entityType: string
  entityId: number | null
  fieldName: string
  refetchQueries: string[]
}

const UserTranslation: React.FC<Props> = ({ entityType, entityId, fieldName, refetchQueries, children }) => {
  const { t, i18n } = useTranslation()
  const { enqueueSnackbar } = useSnackbar()
  const classes = useStyles()
  const [isActive, setIsActive] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const meQuery = useMeQuery({
    fetchPolicy: 'cache-first',
    nextFetchPolicy: 'cache-first',
  })
  const translationsQuery = useUserTranslationsQuery({
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    skip: !isActive || !entityId || i18n.language === 'en',
    variables: {
      first: 10,
      entityType,
      entityId,
      fieldName,
      user: meQuery.data?.me?.id,
    },
  })
  const [updateTranslation, { loading: updateTranslationLoading }] = useUpdateTranslationMutation()
  const userPermissions = maybe<string[]>(() => meQuery.data.me.permissions, [])
  const translator = userPermissions.includes(`translation.translator_${i18n.language}`)
  const mainTranslator = userPermissions.includes(`translation.edit_user_translation`)
  if (!entityId || i18n.language === 'en' || !(translator || mainTranslator)) return <>{children}</>

  const translations = maybe<UserTranslationFragment[]>(
    () => translationsQuery.data.userTranslations.edges.map(edge => edge.node),
    []
  )
  const onUpdateTranslation = async (id: string, values: TranslationFormData) => {
    try {
      await updateTranslation({
        variables: { input: { id, currentText: values.currentText, status: values.status } },
        refetchQueries,
      })
      setIsOpen(false)
    } catch (e) {
      enqueueSnackbar(e.message, { variant: 'error' })
    }
  }
  const statusToCheck = mainTranslator ? TranslationStatusEnum.Approved : TranslationStatusEnum.FullyTranslated
  return (
    <span onMouseEnter={() => setIsActive(true)}>
      <Tooltip
        classes={{ popper: classes.popper }}
        placement='right-start'
        title={
          <IconButton
            title={USER_TRANSLATION_STATUS_MAPPING(t, translations[0]?.status)}
            sx={{
              color: translations.some(tr => tr.status === statusToCheck) ? 'green' : 'red',
            }}
            size='medium'
            onClick={() => setIsOpen(true)}
            disabled={!translations.length}
          >
            <TranslateIcon />
          </IconButton>
        }
        enterDelay={300}
        leaveDelay={1000}
      >
        <span>{children}</span>
      </Tooltip>
      <UserTranslationDialogForm
        isOpen={isOpen}
        isMainTranslator={mainTranslator}
        setIsOpen={setIsOpen}
        translations={translations}
        onSubmit={onUpdateTranslation}
        isLoading={updateTranslationLoading}
      />
    </span>
  )
}

export default UserTranslation
